/**
 * What the console says about a lift's rules (Akash, 2026-11-02, decision G):
 * the pure half, read by the Devices table, its phone rows, the lift panel and
 * the calendar alike, so none of them can say it another way.
 *
 * Staff never see "closure", "lanes", "breaker" or "scope". The words are Akash's:
 * Hold all tasks · Block lift from robot use · Allow travel between floors; Robot
 * use · Travel between floors; Same floor only.
 *
 * THE CHIP READS WHAT THE ADAPTERS APPLIED, never only what was asked: a block
 * that has not landed does not say the lift is blocked.
 */
import type {
  LiftRuleHoldingRobot,
  LiftRuleLiftView,
  LiftRuleScopeView,
  LiftRulesView,
  LiftRuleWindowKind,
} from '../../api/v1';
import type { StatusKind } from '../../design-system/tokens';
import { personLabel, standaloneLabel } from '../Tasks/calendar/people';
import { robotName } from './lift-reading';

export const ROBOT_USE = 'Robot use';
export const TRAVEL_BETWEEN_FLOORS = 'Travel between floors';
export const SAME_FLOOR_ONLY = 'Same floor only';
export const BLOCKED_FROM_ROBOT_USE = 'Blocked from robot use';
export const ROBOT_USE_ALLOWED = 'Robot use allowed';
export const TRAVEL_ALLOWED = 'Travel between floors allowed';
export const NOT_COMMISSIONED = 'Not commissioned';

export const HOLD_ALL_TASKS = 'Hold all tasks';
export const BLOCK_LIFT_FROM_ROBOT_USE = 'Block lift from robot use';
export const ALLOW_TRAVEL_BETWEEN_FLOORS = 'Allow travel between floors';

export const WINDOW_KIND_WORDS: Readonly<Record<LiftRuleWindowKind, string>> = {
  block_use: BLOCK_LIFT_FROM_ROBOT_USE,
  allow_travel: ALLOW_TRAVEL_BETWEEN_FLOORS,
};

/** What a calendar run does, as a band or a list row says it. */
export const WINDOW_BAND_WORDS: Readonly<Record<LiftRuleWindowKind, string>> = {
  block_use: BLOCKED_FROM_ROBOT_USE,
  allow_travel: TRAVEL_ALLOWED,
};

// ----------------------------------------------------------------------------
// The floors a block cuts off.
// ----------------------------------------------------------------------------

const LEVEL_RE = /^(.*?)(\d+)$/;

/** Next to each other: side by side on this lift's own list of floors when it
 *  serves both (a hotel with no L13 still has L6–L17), else by number. */
function nextTo(a: string, b: string, served: readonly string[]): boolean {
  const ia = served.indexOf(a);
  const ib = served.indexOf(b);
  if (ia >= 0 && ib >= 0) return ib === ia + 1;
  const ma = LEVEL_RE.exec(a);
  const mb = LEVEL_RE.exec(b);
  return !!ma && !!mb && ma[1] === mb[1] && Number(mb[2]) === Number(ma[2]) + 1;
}

function levelRank(level: string, served: readonly string[]): [number, string, number] {
  const at = served.indexOf(level);
  if (at >= 0) return [at, '', 0];
  const m = LEVEL_RE.exec(level);
  return [served.length, m ? m[1]! : level, m ? Number(m[2]) : 0];
}

/**
 * "L6–L17", "L1–L3, L5 and L6", "L17". A run of three or more collapses; two
 * side by side read better as themselves.
 */
export function levelsWords(levels: readonly string[], served: readonly string[] = []): string {
  const sorted = [...new Set(levels)].sort((a, b) => {
    const ra = levelRank(a, served);
    const rb = levelRank(b, served);
    return ra[0] - rb[0] || ra[1].localeCompare(rb[1]) || ra[2] - rb[2];
  });
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let end = i;
    while (end + 1 < sorted.length && nextTo(sorted[end]!, sorted[end + 1]!, served)) end += 1;
    if (end - i >= 2) {
      parts.push(`${sorted[i]}–${sorted[end]}`);
    } else {
      for (let k = i; k <= end; k += 1) parts.push(sorted[k]!);
    }
    i = end + 1;
  }
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The warning a supervisor confirms (Akash, 2026-11-03). It fires on nearly every
 * PL3 or SL1 block at sg-nomad-01 on map 1.0.0, so it says what stops and nothing
 * else: no "warning", no "danger". `during` is for a calendar run, which ends by
 * itself.
 */
export function cutOffSentence(
  levels: readonly string[],
  served: readonly string[] = [],
  during = false,
): string {
  const where = levelsWords(levels, served);
  const robots = where ? `Robots on ${where}` : 'Some robots';
  return `${robots} can’t get back to a charger ${during ? 'during this time' : 'until this is released'}.`;
}

// ----------------------------------------------------------------------------
// Who a change is waiting on.
// ----------------------------------------------------------------------------

function listWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "after Phantas-1's ride", "after Phantas-1's and Phantas-2's rides", and the
 *  lift when it is another: a close waits for a robot on board any lift. */
export function afterRideWords(
  robots: readonly LiftRuleHoldingRobot[] | undefined,
  thisLift: string,
): string | null {
  const known = (robots ?? []).filter((r) => r.robot);
  if (known.length === 0) return null;
  const elsewhere = known.some((r) => r.lift && r.lift !== thisLift);
  if (!elsewhere) {
    const names = known.map((r) => `${robotName(r.robot!)}’s`);
    return `after ${listWords(names)} ${known.length === 1 ? 'ride' : 'rides'}`;
  }
  const rides = known.map((r) => {
    const ride = `${robotName(r.robot!)}’s ride`;
    return r.lift && r.lift !== thisLift ? `${ride} in ${r.lift}` : ride;
  });
  return `after ${listWords(rides)}`;
}

// ----------------------------------------------------------------------------
// One scope, read.
// ----------------------------------------------------------------------------

export type LiftRuleClock = (epochMs: number) => string;

export interface LiftRuleWho {
  readonly mySub?: string | null;
  readonly myEmail?: string | null;
}

/** The chip's state, for what each screen does with it. */
export type ScopeState =
  | 'allowed'
  | 'blocked'
  | 'same_floor'
  | 'built_same_floor'
  | 'pending'
  | 'stuck'
  | 'unconfirmed'
  | 'reopening'
  | 'refused'
  | 'latched'
  | 'no_lanes';

export interface ScopeReading {
  readonly state: ScopeState;
  readonly status: StatusKind;
  readonly label: string;
  readonly dashed: boolean;
  /** Whether the switch glyph may be drawn on, off or not at all. */
  readonly switchOn: boolean | null;
  /** The sentence the chip needs, when it needs one. */
  readonly note: string | null;
  /** The same, short enough for a table row. */
  readonly rowNote?: string;
}

function isThroughCar(lift: LiftRuleLiftView): boolean {
  return (lift.graph.two_sided_levels ?? 0) > 0;
}

export function servesFloors(lift: LiftRuleLiftView): boolean {
  return (lift.graph.levels ?? 0) > 1;
}

/** Does the panel show a Travel between floors switch? §2: a single-door lift's
 *  shows only when something is holding it, since Robot use covers the rest. */
export function showsTravelSwitch(lift: LiftRuleLiftView): boolean {
  if (!servesFloors(lift) && lift.between_floors.available !== true) return false;
  if (isThroughCar(lift) || !lift.between_floors.available) return true;
  return lift.between_floors.desired === 'closed' || Boolean(lift.between_floors.hold);
}

export function readScope(lift: LiftRuleLiftView, view: LiftRuleScopeView): ScopeReading {
  const isUse = view.scope === 'use';
  const closedLabel = isUse
    ? BLOCKED_FROM_ROBOT_USE
    : isThroughCar(lift)
      ? SAME_FLOOR_ONLY
      : 'Travel between floors blocked';
  const openLabel = isUse ? ROBOT_USE_ALLOWED : TRAVEL_ALLOWED;
  const after = afterRideWords(view.holding_robots, lift.lift);

  if (!view.available) {
    return isUse
      ? {
          state: 'no_lanes',
          status: 'offline',
          label: 'Not on robot routes',
          dashed: true,
          switchOn: null,
          note: 'The robots’ map has no way through this lift, so there is nothing to switch.',
        }
      : {
          state: 'built_same_floor',
          status: 'idle',
          label: SAME_FLOOR_ONLY,
          dashed: false,
          switchOn: null,
          note: 'Robots cross this car on one floor. Travel between floors is built out of this site’s map, so there is nothing to switch.',
        };
  }
  if (view.breaker === 'latched') {
    return {
      state: 'latched',
      status: 'error',
      label: 'Kept closed for safety',
      dashed: false,
      switchOn: view.applied === 'closed' ? false : null,
      note: null,
    };
  }
  if (view.refused && view.applied !== 'open') {
    return {
      state: 'refused',
      status: 'warning',
      label: 'Lift refusing robots',
      dashed: false,
      switchOn: false,
      note: 'The lift itself is refusing robots just now, whatever is set here. It opens again by itself once the lift is taking robots.',
    };
  }
  if (view.desired === 'closed') {
    if (view.applied === 'closed' && !view.unconfirmed) {
      return {
        state: isUse || !isThroughCar(lift) ? 'blocked' : 'same_floor',
        status: isUse ? 'warning' : 'idle',
        label: closedLabel,
        dashed: false,
        switchOn: false,
        note: null,
      };
    }
    if (view.pending || view.holding_robots?.length) {
      const who = (view.holding_robots ?? []).filter((r) => r.robot).map((r) => robotName(r.robot!));
      if (view.stuck) {
        return {
          state: 'stuck',
          status: 'error',
          label: 'Block is waiting',
          dashed: false,
          switchOn: null,
          note: `${listWords(who) || 'A robot'} is still using a lift and has stopped moving, so the block can’t start. Check the robot; Recover the lift from its panel if it is stuck.`,
          rowNote: `Waiting on ${listWords(who) || 'a robot'}, which has stopped moving`,
        };
      }
      return {
        state: 'pending',
        status: 'warning',
        label: 'Block is waiting',
        dashed: true,
        switchOn: null,
        note: after ? `Starts ${after}.` : 'Starting in a few seconds.',
      };
    }
    return {
      state: 'unconfirmed',
      status: 'offline',
      label: 'Not confirmed yet',
      dashed: true,
      switchOn: null,
      note: 'The robots’ system has not confirmed this yet. Until it does, robots may still use this lift.',
    };
  }
  // Wanted open.
  if (view.applied === 'open' && !view.unconfirmed) {
    return {
      state: 'allowed',
      status: 'active',
      label: openLabel,
      dashed: false,
      switchOn: true,
      note: null,
    };
  }
  if (view.applied === 'closed' && !view.unconfirmed) {
    return {
      state: 'reopening',
      status: 'idle',
      label: 'Opening again',
      dashed: true,
      switchOn: null,
      note: 'Released. Robots can use it again as soon as the robots’ system has opened it.',
    };
  }
  return {
    state: 'unconfirmed',
    status: 'offline',
    label: 'Not confirmed yet',
    dashed: true,
    switchOn: null,
    note: 'The robots’ system has not confirmed this yet, so this may not be what robots are doing.',
  };
}

/** Who set a block: "you", a name or an address, "a team member". */
export function holdSetBy(view: LiftRuleScopeView, who: LiftRuleWho): string {
  const email = view.hold?.set_by_email ?? null;
  if (email && who.myEmail && email === who.myEmail) return 'you';
  return personLabel({ email }, who.mySub ?? null);
}

/** "until 18:00", "until released", "until the calendar changes at 23:00". */
export function holdUntilWords(view: LiftRuleScopeView, clock: LiftRuleClock): string | null {
  const hold = view.hold;
  if (!hold) return null;
  if (hold.until_kind === 'time' && typeof hold.until_ms === 'number') {
    return `until ${clock(hold.until_ms)}`;
  }
  if (hold.until_kind === 'next_change') {
    return typeof hold.until_ms === 'number'
      ? `until the calendar changes at ${clock(hold.until_ms)}`
      : 'until released: no calendar change is coming';
  }
  return 'until released';
}

/** Why it is this way, in a line: the block, the calendar or the default. */
export function scopeSourceWords(
  view: LiftRuleScopeView,
  clock: LiftRuleClock,
  who: LiftRuleWho,
): string {
  const reason = view.reason ? ` · “${view.reason}”` : '';
  if (view.source === 'manual' && view.hold) {
    const by = standaloneLabel(holdSetBy(view, who));
    return `${holdUntilWords(view, clock) ?? 'until released'} · ${by}${reason}`;
  }
  if (view.source === 'calendar') return `by the calendar${reason}`;
  if (view.source === 'fire') return 'held during the fire alarm';
  if (view.source === 'commissioning') return 'not set up for robots';
  return 'default for this lift';
}

/** "Mon 23:00: travel between floors allowed", or null. */
export function nextChangeWords(view: LiftRuleScopeView, clock: LiftRuleClock): string | null {
  if (typeof view.next_change_ms !== 'number' || !view.next_desired) return null;
  const opens = view.next_desired === 'open';
  const what =
    view.scope === 'use'
      ? opens
        ? 'robot use allowed'
        : 'blocked from robot use'
      : opens
        ? 'travel between floors allowed'
        : 'same floor only';
  return `${clock(view.next_change_ms)}: ${what}`;
}

// ----------------------------------------------------------------------------
// The Devices table's Robots column, and its phone line.
// ----------------------------------------------------------------------------

export interface LiftRobotsCell {
  readonly status: StatusKind;
  readonly label: string;
  readonly small: string | null;
  readonly dashed: boolean;
  /** Tints the row: the rule needs a person. */
  readonly tone: 'warning' | 'critical' | null;
  /** A commissioned lift a supervisor can act on. */
  readonly changeable: boolean;
  /** Robots may not board it now, whatever the reason. */
  readonly blocked: boolean;
}

const NOT_KNOWN: LiftRobotsCell = {
  status: 'offline',
  label: 'Not known',
  small: 'The robots’ system is not reporting its lifts',
  dashed: true,
  tone: null,
  changeable: false,
  blocked: false,
};

export function liftRobotsNotKnownCell(): LiftRobotsCell {
  return NOT_KNOWN;
}

export function liftRobotsCell(
  lift: LiftRuleLiftView,
  clock: LiftRuleClock,
  who: LiftRuleWho,
): LiftRobotsCell {
  if (!lift.commissioned) {
    return {
      status: 'offline',
      label: NOT_COMMISSIONED,
      small: null,
      dashed: true,
      tone: null,
      changeable: false,
      blocked: false,
    };
  }
  const use = readScope(lift, lift.use);
  const small = (parts: ReadonlyArray<string | null>) => parts.filter(Boolean).join(' · ') || null;
  if (use.state !== 'allowed') {
    const blockedNow = use.state === 'blocked' || use.state === 'refused' || use.state === 'latched';
    const needsPerson =
      use.state === 'stuck' || use.state === 'latched'
        ? 'critical'
        : use.state === 'unconfirmed'
          ? 'warning'
          : null;
    return {
      status: use.status,
      label: use.label,
      small: small([
        use.rowNote ?? (use.state === 'pending' ? use.note : null),
        lift.use.source === 'manual' || lift.use.source === 'calendar'
          ? scopeSourceWords(lift.use, clock, who)
          : null,
      ]),
      dashed: use.dashed,
      tone: needsPerson,
      changeable: use.state !== 'no_lanes',
      blocked: blockedNow,
    };
  }
  const travel = readScope(lift, lift.between_floors);
  const next = nextChangeWords(lift.between_floors, clock);
  if (travel.state === 'same_floor' || travel.state === 'built_same_floor') {
    return {
      status: 'idle',
      label: SAME_FLOOR_ONLY,
      small: small([next ? `next ${next}` : null]),
      dashed: false,
      tone: null,
      changeable: true,
      blocked: false,
    };
  }
  if (!servesFloors(lift)) {
    return {
      status: use.status,
      label: use.label,
      small: small([scopeSourceWords(lift.use, clock, who)]),
      dashed: false,
      tone: null,
      changeable: true,
      blocked: false,
    };
  }
  const travelNeedsPerson =
    travel.state === 'stuck' || travel.state === 'latched'
      ? 'critical'
      : travel.state === 'unconfirmed'
        ? 'warning'
        : null;
  return {
    status: travel.state === 'allowed' ? use.status : travel.status,
    label: travel.state === 'allowed' ? use.label : `Travel: ${travel.label}`,
    small: small([
      travel.state === 'allowed' ? 'travel between floors allowed' : travel.note,
      scopeSourceWords(lift.between_floors, clock, who),
    ]),
    dashed: travel.state === 'allowed' ? false : travel.dashed,
    tone: travelNeedsPerson,
    changeable: true,
    blocked: false,
  };
}

/** A lift's entry in the rules, by the name the Devices table shows. */
export function ruleForLift(
  view: LiftRulesView | null | undefined,
  name: string,
): LiftRuleLiftView | null {
  return view?.lifts.find((l) => l.lift === name) ?? null;
}

/** Lifts robots may not board now, for the summary line. */
export function ruleBlockedLifts(view: LiftRulesView | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const lift of view?.lifts ?? []) {
    if (!lift.commissioned) continue;
    const { state } = readScope(lift, lift.use);
    if (state === 'blocked' || state === 'refused' || state === 'latched') out.add(lift.lift);
  }
  return out;
}

/** The calendar's lift choices: which lifts each kind of run can name. Defaults
 *  are not reported, so `allow_travel` drops only a lift known to be open by
 *  default; the server refuses the rest in words. */
export function calendarLiftChoices(view: LiftRulesView | null | undefined): {
  block_use: string[];
  allow_travel: string[];
} {
  const block_use: string[] = [];
  const allow_travel: string[] = [];
  for (const lift of view?.lifts ?? []) {
    if (!lift.commissioned) continue;
    if (lift.use.available) block_use.push(lift.lift);
    const travel = lift.between_floors;
    const openByDefault = travel.source === 'default' && travel.desired === 'open';
    if (travel.available && !openByDefault) allow_travel.push(lift.lift);
  }
  return { block_use, allow_travel };
}
